import { api } from '@/lib/api/client';
import type { AuthResponse, OnboardingStatus, User } from '@/lib/api/types';

export const authApi = {
  sendOtp: (email: string) =>
    api.post<{ ok: boolean; devHint?: string }>('/auth/send-otp', { email }),
  verifyOtp: (email: string, otp: string) =>
    api.post<AuthResponse>('/auth/verify-otp', { email, otp }),
  login: (email: string, password: string) =>
    api.post<AuthResponse>('/auth/login', {
      email,
      password,
      app: import.meta.env.VITE_APP,
    }),
  completeProfile: (input: { companyName: string; contactName: string; industry: string }) =>
    api.patch<{ user: User; company: { id: number } }>('/auth/complete-profile', input),
  /**
   * Post-login probe so the OnboardingGate has hasCompany/hasBrandKit instead
   * of guessing from the login response (which does not include those flags
   * — the backend looks them up from co_brandkits).
   */
  onboardingStatus: () => api.get<OnboardingStatus>('/auth/onboarding-status'),
  /**
   * Swap a short-lived OAuth exchange code (returned in the redirect query
   * after a successful Google/LinkedIn/Facebook login) for a real JWT + the
   * caller's plan + planAlert context. See ox-platform/auth.service.ts.
   */
  oauthExchange: (code: string) =>
    api.post<
      AuthResponse & { isNewUser: boolean; plan: any; planAlert: any }
    >('/auth/oauth-exchange', { code, app: import.meta.env.VITE_APP }),
  /**
   * Password recovery: ask the backend to email an OTP to the user's
   * recoveryEmail (or primary email). Response is generic — does not reveal
   * whether the account exists.
   */
  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }),
  /**
   * Consume the recovery OTP and set a new password. Backend verifies the OTP
   * from the same store as `verifyOtp` (single-use, expiry-checked).
   */
  resetPassword: (input: { email: string; otp: string; newPassword: string }) =>
    api.post<{ ok: true; message: string }>('/auth/reset-password', input),
  /**
   * Server-side logout: clears the single-session record so the next login
   * from anywhere proceeds without waiting for the 15-min heartbeat window.
   * Fire-and-forget at call sites — the client-side store is cleared
   * separately and the backend record is best-effort.
   */
  logout: () => api.post<{ ok: true }>('/auth/logout', {}),
  /**
   * Explicit signup wizard. `initiate` stashes the company+user payload on
   * the backend and emails a 6-digit OTP. `verify` consumes the OTP, creates
   * the user + company + Free plan, and returns a real JWT so the frontend
   * can auto-login.
   */
  register: {
    initiate: (input: {
      companyName: string;
      firstname: string;
      lastname: string;
      email: string;
      password: string;
    }) =>
      api.post<{ ok: boolean; devHint?: string }>(
        '/auth/register/initiate',
        input,
      ),
    verify: (input: { email: string; otp: string }) =>
      api.post<
        AuthResponse & { isNewUser: boolean; plan: any; planAlert: any }
      >('/auth/register/verify', input),
  },
};
